import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { siteRequestHeaders } from '../../lib/siteRequestHeaders';
import { useAuth } from '../../auth';

export const usePlayerProfile = (userId, username) => {
  const { user } = useAuth();
  const viewer = user?.id;
  const [state, setState] = useState({ profile: null, balances: null, error: '', balanceError: '', loading: true });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setState({ profile: null, balances: null, error: '', balanceError: '', loading: true });
    if (!userId && !username) return;
    const root = `${process.env.REACT_APP_BACKEND_URL}/api/community/profiles`;
    (async () => {
      const resolvedId = userId || (await axios.get(`${root}/by-username/${encodeURIComponent(username)}`, { headers: siteRequestHeaders(), signal: abort.signal })).data.user_id;
      const url = `${root}/${encodeURIComponent(resolvedId)}`;
      const allowed = !!viewer && (viewer === resolvedId || user?.role === 'admin');
      const balanceRequest = allowed ? axios.get(`${url}/balances`, { headers: siteRequestHeaders(), signal: abort.signal })
        .then(({ data }) => ({ balances: data, balanceError: '' })).catch((failure) => ({ balances: null, balanceError: failure.response?.status === 403 ? '' : 'Balances are temporarily unavailable.' })) : Promise.resolve({ balances: null, balanceError: '' });
      const [result, balance] = await Promise.all([axios.get(url, { signal: abort.signal, headers: siteRequestHeaders() }), balanceRequest]);
      if (!abort.signal.aborted) setState({ profile: result.data, ...balance, error: '', loading: false });
    })().catch((failure) => {
      if (!abort.signal.aborted) setState({ profile: null, balances: null, balanceError: '', loading: false, error: failure.response?.status === 404 ? 'This player profile is unavailable.' : 'Unable to load this profile. Please try again.' });
    });
    return () => abort.abort();
  }, [userId, username, viewer, user?.role, revision]);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const canViewBalances = !!viewer && (viewer === state.profile?.user_id || user?.role === 'admin');
  return { ...state, balances: canViewBalances ? state.balances : null, refresh };
};